import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { IonItemSliding, ModalController, NavController } from '@ionic/angular';
import { OrderChangesService } from 'src/app/services/order/order-changes.service';
import { ErrorToastService } from 'src/app/services/error-toast.service';
import { Order } from '../../../dto/order.dto';
import { Status } from '../../../dto/status.enum';
import { OrdersService } from '../../../services/order/orders.service';
import { ActionsModalComponent } from '../../../shared/actions-modal/actions-modal.component';
import {
  ARCHIVED_REQUESTS,
  OPEN_REQUESTS,
  REQUESTS_IN_WORK
} from '../../../utils/constants/router.constants';
import { DaysListComponent } from '../../days-list/days-list.component';
import { ImageService } from '../../../services/image.service';
import { AcceptOrderModalService } from '../../../services/modal/accept-order-modal.service';
import { MenuTabChangeService } from '../../../services/menu-tab-change.service';
import { ActionsModalDto } from '../../../dto/shared.dto';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss']
})
export class OrderComponent implements OnInit, OnChanges {
  @ViewChild('slidingItem') slidingItem: IonItemSliding;
  @Input() order: Order;
  @Output() reloadRequests = new EventEmitter<boolean>();
  @Output() awaitLoading = new EventEmitter<boolean>();

  component: DaysListComponent;
  isAcceptable: boolean;
  isRemovable: boolean;
  inWork: boolean;
  isCompleted: boolean;
  targetUrl = window.location.pathname.split('/')[1];

  orderImagesPath: string = '';

  constructor(
    private modalCtrl: ModalController,
    private ordersService: OrdersService,
    private orderChangesService: OrderChangesService,
    private navCtrl: NavController,
    private errorToastService: ErrorToastService,
    private imageService: ImageService,
    private acceptOrderModalService: AcceptOrderModalService,
    private tabChangeService: MenuTabChangeService
  ) {}

  ngOnInit(): void {
    this.updateStatus(this.order.status);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['order']) {
      const { freightName, status } = this.order;
      this.orderImagesPath +=
        this.imageService.generateOrderImgPath(freightName);
      if (status) {
        this.updateStatus(status);
      }
    }
  }

  getBackgroundColor(): string {
    if (this.isAcceptable) {
      return '#00bfa8';
    }
    if (this.isRemovable) {
      return '#f8627d';
    }
    return '#fff';
  }

  openDaysList(itemId: number) {
    let path;
    switch (this.order.status) {
      case Status.OPEN:
        path = OPEN_REQUESTS;
        break;
      case Status.COMPLETED:
        path = ARCHIVED_REQUESTS;
        break;
      case Status.CLOSED:
        path = ARCHIVED_REQUESTS;
        break;
      default:
        path = REQUESTS_IN_WORK;
        break;
    }

    this.orderChangesService.setOrder(this.order, this.targetUrl);
    this.navCtrl.navigateForward(`${path}/days/${itemId}`);
  }

  async acceptOrder(orderId: number) {
    return await this.acceptOrderModalService.acceptOrderModal(async () => {
      this.awaitLoading.emit(true);
      try {
        await firstValueFrom(this.ordersService.acceptOrder(orderId));

        const orderRes = await firstValueFrom(
          this.ordersService.getOrderById(orderId)
        );
        const createdRequest = orderRes?.success ? orderRes.data : null;

        if (createdRequest) {
          this.orderChangesService.setOrder(createdRequest, REQUESTS_IN_WORK);
          this.orderChangesService.setOrder(createdRequest, OPEN_REQUESTS);

          this.tabChangeService.refreshData.next(OPEN_REQUESTS);

          await this.navCtrl.navigateRoot([`/${REQUESTS_IN_WORK}/days/${orderId}`]);
        } else {
          await this.navCtrl.navigateRoot([`/${REQUESTS_IN_WORK}`]);
        }

        this.awaitLoading.emit(false);
        this.reloadRequests.emit(true);
      } catch (error) {
        this.awaitLoading.emit(false);
        Sentry.captureException(error);
      }
    });
  }

  async removeRequest(orderId: number) {
    const modal = await this.modalCtrl.create({
      component: ActionsModalComponent,
      breakpoints: [0, 1],
      initialBreakpoint: 1,
      componentProps: {
        data: {
          rejectAction: () => this.performRemoveRequest(orderId),
          rejectButton: true,
          rejectButtonText: 'Отказаться',
          message: `Отказываясь от заявки, вы больше не сможете продолжить работу по ней.
            Данное действие нельзя отменить. Хотите отказаться от заявки?`,
          title: 'Отказаться от заявки?',
          imageSrc: '../../../assets/images/trash.svg'
        } as ActionsModalDto
      }
    });
    await modal.present();

    const { role, data } = await modal.onDidDismiss();

    // Отказ прошёл — заявка ушла с экрана. Отказ отклонён из-за подписанного поручения (409) — заявка уже
    // в работе или вот-вот будет: перечитываем список, чтобы экран показал её актуальное состояние.
    const declineRejectedAsSigned = (data?.error as { status?: number } | undefined)?.status === 409;
    if (role === 'reject' && (!data?.error || declineRejectedAsSigned)) {
      this.reloadRequests.emit(true);
    }
  }

  updateStatus(status: Status): void {
    this.isAcceptable = status === Status.OPEN;
    this.isRemovable = status === Status.ACCEPTED;
    this.inWork = status === Status.IN_WORK;
    this.isCompleted = status === Status.COMPLETED;
  }

  calculateTotalFee() {
    return this.order.requestedWeightInTons * this.order.forwarderFee;
  }

  generateLoadingsDate() {
    const loadingDayCount = this.order.shipmentDates.length;

    if (loadingDayCount > 1) {
      return `c ${this.formatDate(
        this.order.shipmentDates[0]
      )} до ${this.formatDate(this.order.shipmentDates[loadingDayCount - 1])}`;
    }

    if (loadingDayCount === 1) {
      return `${this.formatDate(this.order.shipmentDates[0])}`;
    }

    return '';
  }

  formatDate(dateString: string): string {
    const [year, month, day] = dateString.split('-');
    return `${day}.${month}.${year}`;
  }

  private async performRemoveRequest(orderId: number): Promise<void> {
    await firstValueFrom(this.ordersService.declineOrder(orderId));
  }
}
